/**
 * Dropping media onto the timeline: library items, chapters, and files straight from the OS.
 *
 * Target resolution is pure (`resolveDropTarget`) so it can be reused for the live preview while
 * dragging and for the actual drop. Items go to the track under the pointer when compatible,
 * otherwise to the nearest compatible free track, otherwise onto a new track.
 */
import {
  addTrack,
  clipEnd,
  createClipFromMedia,
  createSequenceClip,
  insertClip,
  secondsToFrame,
  sequenceDuration,
  type Id,
  type MediaAsset,
  type Project,
  type Sequence,
  type Track,
  type TrackKind,
} from '@ve/core';
import { create } from 'zustand';
import { activeDrag, DND_MEDIA, DND_SEQUENCE } from '../../lib/dnd.ts';
import { activeSequenceId, edit, getProject, useEditor } from '../../state/editor-store.ts';
import { importMedia } from '../../state/session.ts';
import type { ImportSource } from '../../media/types.ts';
import { snapTargets } from './interactions.ts';

/** What is being dragged, as far as it can be known during `dragover`. */
export type DropPayload =
  | { type: 'media'; mediaId: Id; kind: TrackKind; frames: number; label: string }
  | { type: 'sequence'; sequenceId: Id; frames: number; label: string }
  | { type: 'files'; kind: TrackKind; count: number; label: string };

/** Where the pointer is: a track row, one of the "new track" zones, or nowhere specific. */
export type DropOver = { trackId: Id } | { newTrack: TrackKind } | null;

export interface DropTarget {
  /** `null` ⇒ a new track of `kind` is created. */
  trackId: Id | null;
  kind: TrackKind;
  frame: number;
}

/** Placeholder length for files whose duration is unknown until uploaded and probed. */
export const UNKNOWN_DURATION_SECONDS = 5;

export function payloadKind(p: DropPayload): TrackKind {
  return p.type === 'sequence' ? 'video' : p.kind;
}

/** Reads the drag payload. Works during dragover (types/items only) and on drop. */
export function readPayload(dt: DataTransfer, project: Project): DropPayload | null {
  const fps = project.settings.fps;
  if (dt.types.includes(DND_MEDIA)) {
    const asset = project.media[activeDrag.mediaId ?? dt.getData(DND_MEDIA)];
    if (!asset) return null;
    const frames = createClipFromMedia(asset, project.settings, 0).duration;
    return {
      type: 'media',
      mediaId: asset.id,
      kind: asset.kind === 'audio' ? 'audio' : 'video',
      frames,
      label: asset.name,
    };
  }
  if (dt.types.includes(DND_SEQUENCE)) {
    const seq = project.sequences[activeDrag.sequenceId ?? dt.getData(DND_SEQUENCE)];
    if (!seq) return null;
    const frames =
      Math.max(1, sequenceDuration(project, seq)) || secondsToFrame(UNKNOWN_DURATION_SECONDS, fps);
    return { type: 'sequence', sequenceId: seq.id, frames, label: seq.name };
  }
  if (dt.types.includes('Files')) {
    const items = [...dt.items].filter((i) => i.kind === 'file');
    const types = items.map((i) => i.type);
    // MIME types may be empty for unusual formats; treat unknown as visual (server decides).
    const allAudio = types.length > 0 && types.every((t) => t.startsWith('audio/'));
    const count = Math.max(1, items.length);
    return {
      type: 'files',
      kind: allAudio ? 'audio' : 'video',
      count,
      label: count === 1 ? (dt.files[0]?.name ?? 'File') : `${count} files`,
    };
  }
  return null;
}

function isFree(track: Track, start: number, end: number): boolean {
  return track.clips.every((c) => clipEnd(c) <= start || c.start >= end);
}

/** Snaps a drop position to edit points, markers and the playhead (start or end edge). */
export function snapDropFrame(seq: Sequence, frame: number, frames: number, pxPerFrame: number): number {
  const threshold = 8 / pxPerFrame;
  let best = frame;
  let bestDist = threshold;
  for (const t of snapTargets(seq, new Set())) {
    for (const candidate of [t, t - frames]) {
      const d = Math.abs(candidate - frame);
      if (candidate >= 0 && d < bestDist) {
        best = candidate;
        bestDist = d;
      }
    }
  }
  return Math.max(0, Math.round(best));
}

export function resolveDropTarget(
  seq: Sequence,
  over: DropOver,
  kind: TrackKind,
  frame: number,
  frames: number,
): DropTarget {
  if (over && 'newTrack' in over) return { trackId: null, kind, frame };
  const list = kind === 'video' ? seq.videoTracks : seq.audioTracks;
  const hovered = over ? list.find((t) => t.id === over.trackId) : undefined;
  if (hovered && !hovered.locked) return { trackId: hovered.id, kind, frame };
  // Incompatible or locked track under the pointer, or no track: pick the best compatible one.
  const unlocked = list.filter((t) => !t.locked);
  const free = (kind === 'video' ? [...unlocked].reverse() : unlocked).find((t) =>
    isFree(t, frame, frame + frames),
  );
  if (free) return { trackId: free.id, kind, frame };
  if (over === null) return { trackId: null, kind, frame };
  return { trackId: unlocked[0]?.id ?? null, kind, frame };
}

// ---------------------------------------------------------------------------------------------
// Upload placeholders shown on the timeline while dropped files are uploading
// ---------------------------------------------------------------------------------------------

export interface PendingDrop {
  id: string;
  trackId: Id;
  frame: number;
  frames: number;
  name: string;
  progress: number;
}

export const usePendingDrops = create<{ drops: PendingDrop[] }>()(() => ({ drops: [] }));

function setDrop(id: string, patch: Partial<PendingDrop> | null) {
  usePendingDrops.setState((s) => ({
    drops:
      patch === null
        ? s.drops.filter((d) => d.id !== id)
        : s.drops.map((d) => (d.id === id ? { ...d, ...patch } : d)),
  }));
}

// ---------------------------------------------------------------------------------------------
// Performing drops
// ---------------------------------------------------------------------------------------------

/** Places a library item or chapter in one undoable edit (creating a track if needed). */
export function dropLibraryItem(
  payload: DropPayload,
  target: DropTarget,
  mode: 'overwrite' | 'insert',
): void {
  const seqId = activeSequenceId();
  const placed = edit(`Add ${payload.label}`, (p) => {
    const trackId = target.trackId ?? addTrack(p, seqId, target.kind);
    if (payload.type === 'media') {
      return insertClip(
        p,
        trackId,
        createClipFromMedia(p.media[payload.mediaId]!, p.settings, target.frame),
        mode,
      );
    }
    if (payload.type === 'sequence') {
      const child = p.sequences[payload.sequenceId]!;
      return insertClip(p, trackId, createSequenceClip(child, target.frame, payload.frames), mode);
    }
    return undefined;
  });
  if (placed) useEditor.getState().select({ clipIds: [placed.id] });
}

/**
 * Imports dropped files (via the storage provider) and places them one after another starting at
 * the drop position. Visual files go to the target (or a compatible) video track, audio files to
 * an audio track. Placeholders with progress are shown while files are copied and analysed.
 */
export async function dropSources(
  sourcesPromise: Promise<ImportSource[]>,
  target: DropTarget,
): Promise<void> {
  const sources = await sourcesPromise;
  if (sources.length === 0) return;
  const project = getProject();
  const seqId = activeSequenceId();
  const placeholderFrames = secondsToFrame(UNKNOWN_DURATION_SECONDS, project.settings.fps);

  // Make sure the primary target track exists so placeholders have a row to live in.
  const trackId = target.trackId ?? edit('Add track', (p) => addTrack(p, seqId, target.kind)) ?? null;
  if (!trackId) return;

  const drops: PendingDrop[] = sources.map((source, i) => ({
    id: crypto.randomUUID(),
    trackId,
    frame: target.frame + i * placeholderFrames,
    frames: placeholderFrames,
    name: source.kind === 'handle' ? source.handle.name : source.name,
    progress: 0,
  }));
  usePendingDrops.setState((s) => ({ drops: [...s.drops, ...drops] }));

  let assets: MediaAsset[] = [];
  try {
    assets = await importMedia(
      sources.map((source) => ({ source })),
      (index, progress) => setDrop(drops[index]!.id, { progress }),
    );
  } finally {
    for (const d of drops) setDrop(d.id, null);
  }
  if (assets.length === 0) return;

  const placedIds = edit(assets.length > 1 ? `Add ${assets.length} clips` : `Add ${assets[0]!.name}`, (p) => {
    const seq = p.sequences[seqId]!;
    const cursor: Record<TrackKind, number> = { video: target.frame, audio: target.frame };
    const ids: Id[] = [];
    for (const imported of assets) {
      const asset = p.media[imported.id]!;
      const kind: TrackKind = asset.kind === 'audio' ? 'audio' : 'video';
      const clip = createClipFromMedia(asset, p.settings, cursor[kind]);
      let destination = kind === target.kind ? trackId : undefined;
      if (!destination) {
        const list = kind === 'video' ? seq.videoTracks : seq.audioTracks;
        destination =
          list.find((t) => !t.locked && isFree(t, clip.start, clip.start + clip.duration))?.id ??
          addTrack(p, seqId, kind);
      }
      ids.push(insertClip(p, destination, clip).id);
      cursor[kind] += clip.duration;
    }
    return ids;
  });
  if (placedIds) useEditor.getState().select({ clipIds: placedIds });
}
